import { addElementToArray, arrayMove, FDate } from '@fold-dev/core'
import { byEnd, byPosition, byPriority, byStart, byTitle } from '../../helpers'
import { emit } from '../../contexts/websocket.context'
import { EventName } from '../../../../api/src/shared'
import { useAppStore } from '../../store/app'

export function findAncestorIds(tasks, targetId, path = []) {
    for (const task of tasks) {
        if (task.id === targetId) {
            return [...path, task.id]
        }
        if (task.tasks?.length) {
            const result = findAncestorIds(task.tasks, targetId, [...path, task.id])
            if (result.length) return result
        }
    }
    return []
}

export const notifyAncestors = (tasks, targetIds) => {
    if (!Array.isArray(targetIds)) return

    targetIds.filter(Boolean).forEach((id) => {
        findAncestorIds(tasks, id).forEach((ancestorId) => {
            emit(ancestorId, EventName.reloadSubtasks, ancestorId)
        })
    })
}

export function replaceNestedTasks(obj, taskIdToReplace, newTasks) {
    if (Array.isArray(obj.tasks)) {
        return {
            ...obj,
            tasks: obj.tasks.map((task) => {
                if (task.id === taskIdToReplace) {
                    return { ...task, tasks: newTasks } // Replace the tasks array
                }
                return replaceNestedTasks(task, taskIdToReplace, newTasks) // Recursively check for nested tasks
            }),
        }
    }
    return obj
}

export function findTaskByIdFromTasks(tasks, taskId) {
    for (const task of tasks) {
        if (task.id === taskId) {
            return task
        }

        // Recursively search in nested tasks
        const foundTask = findTaskByIdFromTasks(task.tasks, taskId)
        if (foundTask) {
            return foundTask
        }
    }

    return null // Task not found
}

export function findTaskById(sections, taskId) {
    for (const section of sections) {
        if (!section.tasks) return null

        for (const task of section.tasks) {
            if (task.id === taskId) {
                return task
            }

            // Recursively search in nested tasks
            const foundTask = findTaskById([task], taskId)
            if (foundTask) {
                return foundTask
            }
        }
    }

    return null // Task not found
}

export function findSubtaskCount(tasks, taskId) {
    function searchTasks(tasks) {
        for (const task of tasks) {
            if (task.id === taskId) {
                return flattenTasks(task).length - 1 // because it will count the current task as 1
            }
            if (task.tasks) {
                const result = searchTasks(task.tasks)
                if (result !== null) return result
            }
        }
        return null
    }

    const result = searchTasks(tasks)
    return result !== null ? result : 0
}

export function findChildTasksByTaskId(obj, taskId) {
    if (obj.id === taskId) {
        return obj.tasks // Return the tasks array if the id matches
    }

    if (Array.isArray(obj.tasks)) {
        for (const task of obj.tasks) {
            const result = findChildTasksByTaskId(task, taskId)
            if (result) {
                return result // Return if found in the nested tasks
            }
        }
    }

    return null // Return null if no match is found
}

export const flattenTasks = (task) => {
    // Flatten the current task's nested tasks
    const flattened = task.tasks ? task.tasks.flatMap(flattenTasks) : []

    // Return the current task and its flattened sub-tasks
    return [task, ...flattened]
}

export const getUpcomingSectionDay = (sectionId, sections) => {
    return sections.find((section) => section.id == sectionId)?.day
}

export const removeTasksUsers = (tasks, userId) => {
    const removeTaskUser = (task) => {
        if (task.users) {
            task.users = task.users.filter((user) => user.id !== userId)
        }

        if (task.tasks) {
            task.tasks = task.tasks.map(removeTaskUser)
        }

        return task
    }

    return tasks.map(removeTaskUser)
}

export const updateTasksUsers = (tasks, update) => {
    const updateTaskUsers = (task) => {
        if (task.users) {
            task.users = task.users.map((user) => (user.id === update.id ? { ...user, ...update } : user))
        }

        if (task.tasks) {
            task.tasks = task.tasks.map(updateTaskUsers)
        }

        return task
    }

    return tasks.map(updateTaskUsers)
}

export const removeTasksLabels = (tasks, labelId) => {
    const removeTaskLabel = (task) => {
        // Remove the label with the matching id
        if (task.labels) {
            task.labels = task.labels.filter((label) => label.id !== labelId)
        }

        // Recursively remove the label from subtasks
        if (task.tasks) {
            task.tasks = task.tasks.map(removeTaskLabel)
        }

        return task
    }

    // Iterate through all tasks and remove the label
    return tasks.map(removeTaskLabel)
}

export const updateTasksLabels = (tasks, update) => {
    const updateTaskLabels = (task) => {
        if (task.labels) {
            task.labels = task.labels.map((label) => (label.id === update.id ? { ...label, ...update } : label))
        }

        if (task.tasks) {
            task.tasks = task.tasks.map(updateTaskLabels)
        }

        return task
    }

    return tasks.map(updateTaskLabels)
}

export const getSectionIndex = (id: string) => +id.split('-')[1]

export const hasSubtasks = (task) => {
    if (!task.tasks) return false
    if (task.tasks.length == 0) return false
    return true
}

export const buildTaskTreeOld = (dataset) => {
    const hashTable = Object.create(null)
    dataset.forEach((aData) => (hashTable[aData.id] = { ...aData, tasks: [], indent: 0 }))
    const dataTree = []

    const setIndent = (task, currentIndent) => {
        task.indent = currentIndent
        task.tasks.forEach((childTask) => setIndent(childTask, currentIndent + 1))
    }

    dataset.forEach((aData) => {
        if (aData.parentTaskId) {
            hashTable[aData.parentTaskId].tasks.push(hashTable[aData.id])
        } else {
            dataTree.push(hashTable[aData.id])
        }
    })

    dataTree.forEach((rootTask) => setIndent(rootTask, 0))

    return dataTree
}

export const buildTaskTree = (dataset) => {
    const hashTable = {}
    const dataTree = []

    dataset.forEach((aData) => (hashTable[aData.id] = { ...aData, tasks: [], indent: 0 }))

    const setIndent = (task, currentIndent) => {
        task.indent = currentIndent
        task.tasks.forEach((childTask) => setIndent(childTask, currentIndent + 1))
    }

    dataset.forEach((aData) => {
        if (aData.parentTaskId) {
            if (hashTable[aData.parentTaskId]) {
                // If parentTaskId is found, add the task to its parent's tasks
                hashTable[aData.parentTaskId].tasks.push(hashTable[aData.id])
            } else {
                // If parentTaskId is not found, add the task as a root entry
                dataTree.push(hashTable[aData.id])
            }
        } else {
            // If no parentTaskId, add the task as a root entry
            dataTree.push(hashTable[aData.id])
        }
    })

    dataTree.forEach((rootTask) => setIndent(rootTask, 0))

    return dataTree
}

export const flattenArray = (arr, indent1 = 0) => {
    const flattenedArray: any = []
    let root = true

    const _flattenArray = (arr, indent) => {
        ;(arr || []).map((task) => {
            root = indent == indent1
            let taskWithoutSubtasks = { ...task, indent, root }
            delete taskWithoutSubtasks.tasks
            flattenedArray.push(taskWithoutSubtasks)
            if (task?.tasks) _flattenArray(task.tasks, indent + 1)
        })
    }

    _flattenArray(arr, indent1)
    return flattenedArray
}

export const flattenArrayWithLevel = (arr) => {
    const flattenedArray: any = []
    let root = true

    const flattenArray = (arr, level = 1) => {
        arr.map((task) => {
            // TODO: last task level is NaN ?
            let taskWithoutSubtasks = { ...task, level, root }
            delete taskWithoutSubtasks.tasks
            root = false
            flattenedArray.push(taskWithoutSubtasks)
            if (task.tasks) flattenArray(task.tasks, level + 1)
        })
    }

    flattenArray(arr)
    return flattenedArray
}

export const flattenSectionsTasks = (sections) =>
    sections.reduce((acc, { tasks }) => [...acc, ...flattenArray(tasks)], [])

export const nestArray = (flatArray) => {
    const result = []
    const stack = []

    flatArray.forEach((task) => {
        task.tasks = []

        while (stack.length > 0 && stack[stack.length - 1].indent >= task.indent) {
            stack.pop()
        }

        if (stack.length > 0) {
            stack[stack.length - 1].tasks.push(task)
        } else {
            result.push(task)
        }

        stack.push(task)
    })

    return result
}

export const nestObjectsByLevel = (array) => {
    return array
        .reduce(
            (r, { level, ...rest }) => {
                // TODO: investigate why level is being saved as NaN in flattenArray() above
                // if it's NaN then we use 1 (root) level
                const levelNumber = isNaN(level) ? 1 : level
                const value = { ...rest, tasks: [] }
                r[levelNumber] = value.tasks
                r[levelNumber - 1].push(value)
                return r
            },
            [[]]
        )
        .shift()
}

export const findTask = (data, taskId) => {
    let task: any = null

    const findTask = (tasks = [], taskId) => {
        tasks.map((t: any) => {
            if (t.id == taskId) task = t
            if (!!t.tasks) findTask(t.tasks, taskId)
        })
    }

    findTask(data, taskId)

    return task
}

export function countEntriesBetweenIds(array, id1, id2) {
    const index1 = array.findIndex((item) => item.id === id1)
    const index2 = array.findIndex((item) => item.id === id2)

    if (index1 === -1 || index2 === -1) {
        return -1
    }

    return Math.abs(index1 - index2) - 1
}

export const findSubtaskAmount = (tasks = []) => {
    let subtasks = 0

    const st = (tasks = []) => {
        tasks.map(({ tasks = [] }) => {
            subtasks++
            if (!!tasks.length) st(tasks)
        })
    }

    st(tasks)

    return subtasks
}

export const removeTask = (data, taskId) => {
    const _removeTask = (tasks = [], taskId) => {
        return tasks.filter((t: any) => t.id != taskId).map((t: any) => ({ ...t, tasks: _removeTask(t.tasks, taskId) }))
    }

    return data.map((section) => ({
        ...section,
        tasks: _removeTask(section.tasks, taskId),
    }))
}

export const removeTasks = (t, tasks = []) => {
    if (!tasks) return []

    return [...tasks]
        .filter((task: any) => task.id != t.id)
        .map((task: any) => {
            return {
                ...task,
                tasks: removeTasks(t, task.tasks),
            }
        })
}

export const removeCompletedTasks = (tasks = []) => {
    if (!tasks) return []

    return [...tasks]
        .filter((task: any) => !task.complete)
        .map((task: any) => {
            return {
                ...task,
                tasks: removeCompletedTasks(task.tasks),
            }
        })
}

export const removeTaskSelection = (ids, tasks = []) => {
    if (!tasks) return []
    return [...tasks]

        .filter((task: any) => !ids.includes(task.id))
        .map((task: any) => {
            return {
                ...task,
                tasks: removeTaskSelection(ids, task.tasks),
            }
        })
}

export const sortSectionTasks = (sections, sortFunction) => {
    return sections.map((s) => ({ ...s, tasks: (s.tasks || []).sort(sortFunction) }))
}

export const updateTasks = (t, tasks = []) => {
    return (tasks ?? []).map((task: any) => {
        if (task.id == t.id) {
            return { ...task, ...t }
        } else {
            return { ...task, tasks: updateTasks(t, task.tasks) }
        }
    })
}

export const updateManyTasks = (targetTasks, updatedTargetTasks) => {
    return targetTasks.map((t) => {
        const task = updatedTargetTasks.find((task) => task.id == t.id)
        if (task) {
            return {
                ...t,
                ...task,
            }
        } else {
            return t
        }
    })
}

export const updateMultipleTasks = (updateObj, taskIds = [], tasks = []) => {
    return (tasks ?? []).map((task: any) => {
        if (taskIds.includes(task.id)) {
            return {
                ...task,
                tasks: updateMultipleTasks(updateObj, taskIds, task.tasks),
                ...updateObj,
            }
        } else {
            return {
                ...task,
                tasks: updateMultipleTasks(updateObj, taskIds, task.tasks),
            }
        }
    })
}

export const replaceSubtasksInSection = (sections, sectionId, taskId, tasks) => {
    return sections.map((section) => {
        if (section.id == sectionId) {
            return replaceNestedTasks(section, taskId, tasks)
        } else {
            return section
        }
    })
}

export const addTasktoSection = (sections, sectionIndex, task) => {
    return sections.map((section, index) =>
        sectionIndex == index
            ? {
                  ...section,
                  tasks: [...section.tasks, task],
              }
            : section
    )
}

export const addTaskstoSection = (sections, sectionIndex, tasks) => {
    return sections.map((section, index) =>
        sectionIndex == index
            ? {
                  ...section,
                  tasks: [...section.tasks, ...tasks],
              }
            : section
    )
}

export const getSortFunction = (sort) => {
    switch (sort) {
        case 'title':
            return byTitle
        case 'start':
            return byStart
        case 'end':
            return byEnd
        case 'priority':
            return byPriority
        default:
            return byPosition
    }
}

export const addTaskToTopOfSubtasks = (tasks, id, newTask) => {
    return tasks.map((task: any) => {
        if (task.id == id) {
            return {
                ...task,
                editable: false,
                addBelow: false,
                tasks: task.tasks ? [newTask, ...task.tasks] : [newTask],
            }
        } else {
            return task
        }
    })
}

export const addTaskToSubtasks = (tasks = [], newTask, id, shouldIndent) => {
    const taskIndex = tasks.findIndex((task: any) => task.id == id)
    const taskPresent = taskIndex != -1

    if (taskPresent) {
        if (shouldIndent) {
            return addTaskToTopOfSubtasks(tasks, id, newTask)
        } else {
            return addElementToArray(tasks, taskIndex + 1, newTask)
        }
    } else {
        return tasks.map((task: any) => {
            return {
                ...task,
                tasks: addTaskToSubtasks(task.tasks || [], newTask, id, shouldIndent),
            }
        })
    }
}

export const addElements = (arr, index, ...newElements) => [...arr.slice(0, index), ...newElements, ...arr.slice(index)]

export const getTargetIndex = ({ target, origin }) => {
    return target.moveDirection == 'up'
        ? target.index > origin.index && target.areaId == origin.areaId
            ? target.index - 1
            : target.index
        : target.index > origin.index && target.areaId == origin.areaId
        ? target.index - 1
        : target.index
}

export const extractIds = (tasks) => {
    return tasks.reduce((ids, task) => {
        ids.push(task.id)
        if (task.tasks && task.tasks.length > 0) {
            ids.push(...extractIds(task.tasks))
        }
        return ids
    }, [])
}

export const findFirstLowerIndentTask = (tasks, startIndex, currentIndent) => {
    // this breaks if the drag index is the very last index:
    // if (!tasks[startIndex]) return currentIndent
    if (tasks?.length == 0) return currentIndent
    for (let i = startIndex - 1; i >= 0; i--) {
        if (tasks[i].indent < currentIndent) {
            return tasks[i]
        }
    }

    return null
}

export const getDaysBetween = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const dayArray = []

    for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
        dayArray.push(new Date(d))
    }

    return dayArray
}

export const FRONTEER_FILTERS = 'FRONTEER_FILTERS'

export const getAllFilters = () => JSON.parse(window.localStorage.getItem(FRONTEER_FILTERS) || '{}')

export const getSavedFilters = (key) => getAllFilters()[key] || {}

export const createDatesForMonth = (date) => {
    const inputDate = new Date(date)

    // Set to the first day of the month
    const start = new Date(inputDate.getFullYear(), inputDate.getMonth(), 1)

    // Set to the last day of the month
    const end = new Date(inputDate.getFullYear(), inputDate.getMonth() + 1, 0)

    return { start: start.toISOString(), end: end.toISOString() }
}

export const createDatesForWeek = (date) => {
    // Create a copy of the input date to avoid mutating the original date
    const inputDate = new Date(date)

    // Find the Sunday of the current week
    const dayOfWeek = inputDate.getDay() // Sunday = 0, Monday = 1, ..., Saturday = 6
    const sunday = new Date(inputDate.setDate(inputDate.getDate() - dayOfWeek))

    // Calculate the Saturday of the current week
    const saturday = new Date(sunday)
    saturday.setDate(saturday.getDate() + 6)

    return { start: sunday.toISOString(), end: saturday.toISOString() }
}

export const createDatesForDay = (date) => {
    const start = new Date(date.setHours(0, 0, 0, 0)).toISOString()
    const end = new Date(date.setHours(23, 59, 59, 999)).toISOString()
    return { start, end }
}

export const createDatesForToday = () => {
    const start = new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
    const end = new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
    return { start, end }
}

export const createDatesForUpcoming = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const start = today.toISOString()
    const end = FDate(new Date()).add(13, 'day').toISOString()
    return { start, end }
}

export const getDates = (type, view, calendarView, date) => {
    switch (type) {
        case 'today':
            return createDatesForToday()
        case 'upcoming':
            return createDatesForUpcoming()
        case 'project':
            if (view == 'calendar') {
                switch (calendarView) {
                    case 0:
                        return createDatesForDay(date)
                    case 1:
                        return createDatesForWeek(date)
                    case 2:
                        return createDatesForMonth(date)
                    default:
                        return createDatesForDay(date)
                }
            } else {
                return null
            }
    }
}

export const moveElementInArrayAlt = (array, originIndex, targetIndex) => {
    return [...arrayMove(array, originIndex, targetIndex)]
}
